// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — bootstrap and the screen machine. Owns which screen is up, the room's Net
// (created ONCE per session, never left for a rematch), and the rAF draw loop that ticks the
// clock and draws. rAF only DRAWS + advances the clock, which is timestamp-driven, so a
// backgrounded tab never strands a round. Rules live in game.ts, the stage in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml, fmtScore, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { createGame, currentPair, drainEvents, tap as tapGame, tick as tickGame, timeLeftMs, type GameEvent, type GameState } from './game';
import { dailySeed, todayUTC, newSeed, seedToCode } from './seed';
import { Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown } from './countdown';
import { drawReaction, pickTap, POLICIES, READER } from './bot';
import { makeRng } from '@ben-gy/game-engine/rng';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'glint';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
const playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number {
  return store.get<number>(bestKey(id), 0) ?? 0;
}
function recordBest(id: string, score: number): boolean {
  const cur = bestFor(id);
  if (score > cur) {
    store.set(bestKey(id), score);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  tick: (dtMs: number) => GameEvent[];
  tap: (symbolId: number) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; score: number; cleared: number; finished: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// Exposed only so the browser-verification hook can drive the REAL input→core path.
let activeBoard: Board | null = null;
let activeTap: ((id: number) => void) | null = null;
let activeState: (() => GameState) | null = null;

function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'hit') sfx.play('coin', { pitch: 0.7 + Math.min(1, e.combo / 12) * 0.9 });
      else if (e.t === 'miss') sfx.play('lose');
      else if (e.t === 'deal') sfx.play('select', { pitch: 1.1 });
      else if (e.t === 'over') sfx.play(e.won ? 'go' : 'explosion');
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'hit') navigator.vibrate(e.milestone ? 18 : 8);
        else if (e.t === 'miss') navigator.vibrate([28, 40, 28]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-score" id="hudScore">0</span>
        <span class="hud-extra" id="hudExtra"></span>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="combo-pill" id="combo" hidden></div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
        <div class="announce" id="announce" hidden></div>
      </div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudScore = content.querySelector<HTMLElement>('#hudScore')!;
  const hudExtra = content.querySelector<HTMLElement>('#hudExtra')!;
  const comboEl = content.querySelector<HTMLElement>('#combo')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  activeBoard = board;
  activeTap = (id) => tapAndFx(id);
  activeState = cfg.state;
  const doResize = (): void => board.resize();
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;
  let hudTick = 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    last = performance.now();
  });

  function tapAndFx(id: number): void {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const events = cfg.tap(id);
    board.onEvents(events, reducedMotion);
    juice(events);
  }

  boardHost.addEventListener('pointerdown', (e) => {
    if (!live || paused || cfg.isOver()) return;
    const id = board.hitTest(e.clientX, e.clientY);
    if (id !== null) {
      e.preventDefault();
      tapAndFx(id);
    }
  });

  function updateHud(state: GameState): void {
    hudScore.textContent = fmtScore(state.score);
    if (mode.finite) hudExtra.textContent = `${state.cleared}/${state.totalPairs}`;
    else if (mode.clock === 'down') hudExtra.textContent = fmtTime(timeLeftMs(state));
    else hudExtra.textContent = `${state.cleared} cards`;

    if (state.combo >= 2) {
      comboEl.hidden = false;
      comboEl.textContent = `×${state.multiplier.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')} · ${state.combo} chain`;
    } else {
      comboEl.hidden = true;
    }

    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => `<span class="rival${r.finished ? ' done' : ''}">${escapeHtml(r.name)} <b>${fmtScore(r.score)}</b></span>`)
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(80, now - last);
    last = now;
    const state = cfg.state();
    if (live && !paused && !cfg.isOver()) {
      const events = cfg.tick(dt);
      board.onEvents(events, reducedMotion);
      juice(events);
    }
    board.frame(state, { dtMs: dt, reducedMotion });
    if (++hudTick % 2 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 720);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (!live || paused || cfg.isOver()) {
      if (e.key === 'p' || e.key === 'P') setPaused(!paused);
      return;
    }
    if (e.key >= '1' && e.key <= '9') {
      const { bottom } = currentPair(cfg.state());
      const idx = Number(e.key) - 1;
      if (idx < bottom.length) {
        e.preventDefault();
        tapAndFx(bottom[idx]);
      }
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      board.moveHighlight(1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      board.moveHighlight(-1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      const id = board.highlightSymbol();
      if (id !== null) {
        e.preventDefault();
        tapAndFx(id);
      }
    } else if (e.key === 'p' || e.key === 'P') {
      setPaused(!paused);
    } else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) {
      cfg.onRestart?.();
    }
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      if (activeBoard === board) {
        activeBoard = null;
        activeTap = null;
        activeState = null;
      }
      cancelAnimationFrame(raf);
      cd.cancel();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    tick: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    tap: (id) => {
      tapGame(soloState!, id);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => startSolo(soloDaily),
  });
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const isNewBest = recordBest(st.mode.id, st.score);
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({ state: st, best: bestFor(mode.id), isNewBest, daily: soloDaily })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${st.score > 0 ? 'score' : 'this deck'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#rfeedback')?.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Glint ${todayUTC()} · ${mode.name}` : `Glint · ${mode.name}`;
  const line = st.score > 0 ? `scored ${fmtScore(st.score)}` : `couldn't crack this ${mode.name} deck`;
  const link = soloDaily ? 'https://glint.benrichardson.dev' : `https://glint.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

async function beginFriends(): Promise<void> {
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same deck. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 760);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    tick: (dt) => rc.tick(dt),
    tap: (id) => rc.tap(id),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, score: p.score, cleared: p.cleared, finished: p.finished })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().find((r) => !r.left);
    if (top && top.score > 0) tally[top.seat] = (tally[top.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.finished && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still playing: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    void beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

function startSeed(seed: number): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    tick: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    tap: (id) => {
      tapGame(soloState!, id);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => startSolo(false),
  });
  onCleanup(() => soloScreen?.destroy());
}

// The browser-verification pass drives this to prove the input→core path and that a bot can
// COMPLETE the objective, WITHOUT a human (rAF is throttled in a hidden tab).
(window as unknown as { __glint?: unknown }).__glint = {
  state(): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const { top, bottom, shared } = currentPair(s);
    return { index: s.index, cleared: s.cleared, score: s.score, combo: s.combo, over: s.over, won: s.won, misses: s.misses, mode: s.mode.id, top, bottom, shared };
  },
  // The client-position of the current shared symbol on the top card — so the browser test
  // can dispatch a REAL click at those coordinates and prove the hit geometry.
  sharedPos(): { x: number; y: number } | null {
    const s = activeState?.() ?? soloState ?? race?.state;
    if (!s || !activeBoard) return null;
    return activeBoard.clientPosOf(currentPair(s).shared);
  },
  // Tap the current shared symbol through the real game path (proves score advances).
  tapShared(): void {
    const s = activeState?.() ?? soloState ?? race?.state;
    if (!s || !activeTap) return;
    activeTap(currentPair(s).shared);
  },
  // Drive the CURRENT game synchronously with a bot for `ms` (rAF is throttled in a hidden tab).
  botRun(ms: number, policyId = READER.id): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const policy = POLICIES.find((p) => p.id === policyId) ?? READER;
    const rng = makeRng(0x51de);
    let attemptAt = drawReaction(policy, rng);
    const cap = s.elapsedMs + ms;
    let guard = 0;
    while (!s.over && s.elapsedMs < cap && guard++ < 100_000) {
      const nextAt = Math.max(attemptAt, s.lockUntilMs);
      const dt = Math.max(1, nextAt - s.elapsedMs);
      if (soloState) tickGame(soloState, dt);
      else race?.tick(dt);
      if (s.over) break;
      const sym = pickTap(s, policy, rng);
      if (soloState) tapGame(soloState, sym);
      else race?.tap(sym);
      attemptAt = s.elapsedMs + drawReaction(policy, rng);
    }
    return { cleared: s.cleared, score: s.score, over: s.over, won: s.won };
  },
};

boot();
